import { database } from './firebase';
import { ref, push, set, onValue, update, remove, query as rtdbQuery, orderByChild, equalTo, get } from 'firebase/database';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

class CombustibleService {
  static async crearRegistro(datosRegistro) {
    try {
      const combustibleRef = ref(database, 'combustible');
      const nuevoRegistroRef = push(combustibleRef);
      
      const registroConMeta = {
        ...datosRegistro,
        id: nuevoRegistroRef.key,
        fechaCreacion: new Date().toISOString(),
        timestamp: Date.now()
      };
      
      await set(nuevoRegistroRef, registroConMeta);
      console.log('Registro de combustible creado con ID:', nuevoRegistroRef.key);
      return nuevoRegistroRef.key;
    } catch (error) {
      console.error('Error al crear registro de combustible:', error);
      throw error;
    }
  }

  static async actualizarRegistro(id, datosActualizacion) {
    try {
      const registroRef = ref(database, `combustible/${id}`);
      const datosActualizados = {
        ...datosActualizacion,
        fechaActualizacion: new Date().toISOString(),
        timestamp: Date.now()
      };
      await update(registroRef, datosActualizados);
      console.log('Registro de combustible actualizado:', id);
    } catch (error) {
      console.error('Error al actualizar registro de combustible:', error);
      throw error;
    }
  }

  static async eliminarRegistro(id) {
    try {
      const registroRef = ref(database, `combustible/${id}`);
      await remove(registroRef);
      console.log('Registro de combustible eliminado:', id);
    } catch (error) {
      console.error('Error al eliminar registro de combustible:', error);
      throw error;
    }
  }

  static suscribirseRegistros(callback) {
    const combustibleRef = ref(database, 'combustible');
    
    return onValue(combustibleRef, (snapshot) => {
      const registros = [];
      snapshot.forEach((child) => {
        registros.push(child.val());
      });
      // Ordenar por fecha descendente
      registros.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      callback(registros);
    }, (error) => {
      console.error('Error al suscribirse a registros de combustible:', error);
    });
  }

  static async obtenerRegistrosPorVehiculo(vehiculoId) {
    try {
      const combustibleRef = ref(database, 'combustible');
      const q = rtdbQuery(combustibleRef, orderByChild('vehiculo'), equalTo(vehiculoId));
      
      const snapshot = await get(q);
      const registros = [];
      snapshot.forEach((child) => {
        registros.push(child.val());
      });
      return registros.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    } catch (error) {
      console.error('Error al obtener registros por vehículo:', error);
      throw error;
    }
  }

  static async obtenerRegistrosPorChofer(choferId) {
    try {
      const combustibleRef = ref(database, 'combustible');
      const q = rtdbQuery(combustibleRef, orderByChild('chofer'), equalTo(choferId));
      
      const snapshot = await get(q);
      const registros = [];
      snapshot.forEach((child) => {
        registros.push(child.val());
      });
      return registros.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    } catch (error) {
      console.error('Error al obtener registros por chofer:', error);
      throw error;
    }
  }

  static async obtenerRegistrosPorRangoFechas(fechaInicio, fechaFin) {
    try {
      const combustibleRef = ref(database, 'combustible');
      const snapshot = await get(combustibleRef);
      const registros = [];
      
      snapshot.forEach((child) => {
        const registro = child.val();
        if (registro.fecha >= fechaInicio && registro.fecha <= fechaFin) {
          registros.push(registro);
        }
      });
      
      return registros.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    } catch (error) {
      console.error('Error al obtener registros por rango de fechas:', error);
      throw error;
    }
  }

  static calcularEstadisticas(registros) {
    if (registros.length === 0) {
      return {
        totalRegistros: 0,
        rendimientoPromedio: 0,
        gastoTotal: 0,
        kilometrosTotales: 0,
        litrosTotales: 0,
        costoPorKilometro: 0,
        rendimientoPorVehiculo: {},
        rendimientoPorChofer: {}
      };
    }

    const rendimientos = registros.map(reg => reg.rendimiento || 0);
    const gastos = registros.map(reg => reg.monto || 0);
    const kilometros = registros.map(reg => reg.kilometrosRecorridos || 0);
    const litros = registros.map(reg => reg.litrosConsumidos || 0);

    const rendimientoPromedio = rendimientos.reduce((a, b) => a + b, 0) / rendimientos.length;
    const gastoTotal = gastos.reduce((a, b) => a + b, 0);
    const kilometrosTotales = kilometros.reduce((a, b) => a + b, 0);
    const litrosTotales = litros.reduce((a, b) => a + b, 0);
    const costoPorKilometro = kilometrosTotales > 0 ? gastoTotal / kilometrosTotales : 0;

    // Calcular rendimiento por vehículo
    const rendimientoPorVehiculo = {};
    registros.forEach(reg => {
      if (!rendimientoPorVehiculo[reg.vehiculo]) {
        rendimientoPorVehiculo[reg.vehiculo] = { total: 0, count: 0 };
      }
      rendimientoPorVehiculo[reg.vehiculo].total += reg.rendimiento || 0;
      rendimientoPorVehiculo[reg.vehiculo].count += 1;
    });

    Object.keys(rendimientoPorVehiculo).forEach(vehiculo => {
      rendimientoPorVehiculo[vehiculo].promedio = 
        rendimientoPorVehiculo[vehiculo].total / rendimientoPorVehiculo[vehiculo].count;
    });

    // Calcular rendimiento por chofer
    const rendimientoPorChofer = {};
    registros.forEach(reg => {
      if (!rendimientoPorChofer[reg.chofer]) {
        rendimientoPorChofer[reg.chofer] = { total: 0, count: 0 };
      }
      rendimientoPorChofer[reg.chofer].total += reg.rendimiento || 0;
      rendimientoPorChofer[reg.chofer].count += 1;
    });

    Object.keys(rendimientoPorChofer).forEach(chofer => {
      rendimientoPorChofer[chofer].promedio = 
        rendimientoPorChofer[chofer].total / rendimientoPorChofer[chofer].count;
    });

    return {
      totalRegistros: registros.length,
      rendimientoPromedio: Math.round(rendimientoPromedio * 100) / 100,
      gastoTotal: Math.round(gastoTotal * 100) / 100,
      kilometrosTotales: Math.round(kilometrosTotales * 100) / 100,
      litrosTotales: Math.round(litrosTotales * 100) / 100,
      costoPorKilometro: Math.round(costoPorKilometro * 100) / 100,
      rendimientoPorVehiculo,
      rendimientoPorChofer
    };
  }

  static generarAlertas(registros, rendimientoPromedio) {
    const alertas = [];
    
    // Agrupar por vehículo
    const porVehiculo = registros.reduce((acc, reg) => {
      if (!acc[reg.vehiculo]) acc[reg.vehiculo] = [];
      acc[reg.vehiculo].push(reg);
      return acc;
    }, {});

    // Analizar cada vehículo
    Object.entries(porVehiculo).forEach(([vehiculo, regs]) => {
      const rendimientos = regs.map(reg => reg.rendimiento || 0);
      const rendimientoPromedioVehiculo = rendimientos.reduce((a, b) => a + b, 0) / rendimientos.length;
      const diferencia = ((rendimientoPromedioVehiculo - rendimientoPromedio) / rendimientoPromedio) * 100;

      if (diferencia < -15) {
        alertas.push({
          tipo: 'warning',
          mensaje: `El vehículo ${vehiculo} está consumiendo un ${Math.abs(diferencia).toFixed(1)}% más que el promedio`,
          icono: '🔴',
          vehiculo,
          diferencia: Math.abs(diferencia)
        });
      } else if (diferencia > 20) {
        alertas.push({
          tipo: 'success',
          mensaje: `El vehículo ${vehiculo} tiene un rendimiento ${diferencia.toFixed(1)}% mejor que el promedio`,
          icono: '🟢',
          vehiculo,
          diferencia
        });
      }
    });

    // Analizar rutas con mayor gasto
    const porRuta = registros.reduce((acc, reg) => {
      const ruta = reg.ruta || 'Sin ruta especificada';
      if (!acc[ruta]) acc[ruta] = { gastos: [], count: 0 };
      acc[ruta].gastos.push(reg.monto);
      acc[ruta].count += 1;
      return acc;
    }, {});

    Object.entries(porRuta).forEach(([ruta, data]) => {
      const gastoPromedio = data.gastos.reduce((a, b) => a + b, 0) / data.gastos.length;
      
      if (gastoPromedio > 500 && data.count > 3) { // Más de $500 promedio y más de 3 registros
        alertas.push({
          tipo: 'info',
          mensaje: `Ruta "${ruta}" presenta mayor gasto por congestión, se recomienda cambiar horario`,
          icono: '⚠️',
          ruta,
          gastoPromedio
        });
      }
    });

    return alertas;
  }

  static exportarPDF(registros) {
    try {
      const doc = new jsPDF();
      
      // Configuración del documento
      doc.setFontSize(20);
      doc.text('Reporte de Consumo de Combustible', 20, 20);
      
      doc.setFontSize(12);
      doc.text(`Fecha de generación: ${new Date().toLocaleDateString()}`, 20, 35);
      doc.text(`Total de registros: ${registros.length}`, 20, 45);

      // Calcular estadísticas
      const estadisticas = this.calcularEstadisticas(registros);
      
      // Agregar estadísticas
      doc.setFontSize(14);
      doc.text('Estadísticas Generales', 20, 60);
      
      doc.setFontSize(10);
      doc.text(`Rendimiento promedio: ${estadisticas.rendimientoPromedio} km/l`, 20, 75);
      doc.text(`Gasto total: $${estadisticas.gastoTotal.toLocaleString()}`, 20, 85);
      doc.text(`Kilómetros totales: ${estadisticas.kilometrosTotales.toLocaleString()} km`, 20, 95);
      doc.text(`Costo por kilómetro: $${estadisticas.costoPorKilometro.toFixed(2)}`, 20, 105);

      // Preparar datos para la tabla
      const tableData = registros.map(reg => [
        new Date(reg.fecha).toLocaleDateString(),
        reg.vehiculo,
        reg.chofer,
        reg.kilometrajeInicial.toLocaleString(),
        reg.kilometrajeFinal.toLocaleString(),
        reg.kilometrosRecorridos.toLocaleString(),
        reg.litrosConsumidos,
        `${reg.rendimiento} km/l`,
        `$${reg.monto.toLocaleString()}`
      ]);

      // Crear tabla
      doc.autoTable({
        startY: 120,
        head: [['Fecha', 'Vehículo', 'Chofer', 'Km Inicial', 'Km Final', 'Km Recorridos', 'Litros', 'Rendimiento', 'Monto']],
        body: tableData,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [102, 126, 234] },
        alternateRowStyles: { fillColor: [248, 249, 250] }
      });

      // Agregar ranking de vehículos
      const finalY = doc.lastAutoTable.finalY + 20;
      doc.setFontSize(14);
      doc.text('Ranking de Vehículos por Rendimiento', 20, finalY);
      
      const rankingVehiculos = Object.entries(estadisticas.rendimientoPorVehiculo)
        .sort((a, b) => b[1].promedio - a[1].promedio)
        .slice(0, 5);

      let yPos = finalY + 15;
      doc.setFontSize(10);
      rankingVehiculos.forEach(([vehiculo, data], index) => {
        doc.text(`${index + 1}. ${vehiculo}: ${data.promedio.toFixed(2)} km/l`, 20, yPos);
        yPos += 10;
      });

      // Guardar el PDF
      const fileName = `reporte_combustible_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      console.log('PDF exportado exitosamente:', fileName);
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      throw error;
    }
  }

  static async obtenerGraficasTendencia(periodo = 'mensual') {
    try {
      const fechaInicio = new Date();
      if (periodo === 'semanal') {
        fechaInicio.setDate(fechaInicio.getDate() - 7);
      } else if (periodo === 'mensual') {
        fechaInicio.setMonth(fechaInicio.getMonth() - 1);
      }

      const combustibleRef = ref(database, 'combustible');
      const snapshot = await get(combustibleRef);
      const registros = [];
      
      snapshot.forEach((child) => {
        const registro = child.val();
        if (registro.fecha >= fechaInicio.toISOString().split('T')[0]) {
          registros.push(registro);
        }
      });

      // Agrupar por fecha
      const porFecha = registros.reduce((acc, reg) => {
        const fecha = reg.fecha;
        if (!acc[fecha]) {
          acc[fecha] = {
            fecha,
            rendimientos: [],
            gastos: [],
            kilometros: []
          };
        }
        acc[fecha].rendimientos.push(reg.rendimiento);
        acc[fecha].gastos.push(reg.monto);
        acc[fecha].kilometros.push(reg.kilometrosRecorridos);
        return acc;
      }, {});

      // Calcular promedios por fecha
      const tendencia = Object.entries(porFecha).map(([fecha, data]) => ({
        fecha,
        rendimientoPromedio: data.rendimientos.reduce((a, b) => a + b, 0) / data.rendimientos.length,
        gastoTotal: data.gastos.reduce((a, b) => a + b, 0),
        kilometrosTotales: data.kilometros.reduce((a, b) => a + b, 0)
      }));

      return tendencia.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    } catch (error) {
      console.error('Error al obtener gráficas de tendencia:', error);
      throw error;
    }
  }

  static async eliminarTodosLosRegistros() {
    try {
      const combustibleRef = ref(database, 'combustible');
      await remove(combustibleRef);
      console.log('Todos los registros de combustible han sido eliminados exitosamente');
      return true;
    } catch (error) {
      console.error('Error al eliminar todos los registros de combustible:', error);
      throw error;
    }
  }
}

export { CombustibleService };